export const PERMISSIONS = {
  // Staff & Security
  STAFF_READ: 'staff:read',
  STAFF_MANAGE: 'staff:manage',
  AUDIT_READ: 'audit:read',

  // Agent Management
  AGENTS_READ: 'agents:read',
  AGENTS_UPDATE: 'agents:update',
  AGENTS_FUND: 'agents:fund',
  AGENTS_MESSAGE: 'agents:message',

  // Services & Pricing
  SERVICES_READ: 'services:read',
  SERVICES_WRITE: 'services:write',
  SERVICES_PRICING: 'services:pricing',
  SERVICES_AVAILABILITY: 'services:availability',
  PROCESSORS_MANAGE: 'processors:manage',
  RESELLING_MANAGE: 'reselling:manage',

  // Financials & Transactions
  DASHBOARD_ANALYTICS: 'dashboard:analytics',
  TRANSACTIONS_READ: 'transactions:read',
  TRANSACTIONS_ANALYTICS: 'transactions:analytics',
  TRANSACTIONS_MANUAL_UPDATE: 'transactions:manual_update',
  TRANSACTIONS_REFUND: 'transactions:refund',

  // Marketing & Support
  CAMPAIGNS_READ: 'campaigns:read',
  CAMPAIGNS_MANAGE: 'campaigns:manage',
  NOTIFICATIONS_READ: 'notifications:read',
  NOTIFICATIONS_MANAGE: 'notifications:manage',
  REFERRAL_MANAGE: 'referral:manage',
  SUPPORT_READ: 'support:read',
  SUPPORT_MANAGE: 'support:manage',

  // System Settings
  SYSTEM_SETTINGS_READ: 'system:settings_read',
  SYSTEM_SETTINGS_UPDATE: 'system:settings_update',
};

/**
 * Checks whether a user object has a required permission.
 * @param {object} user - The logged in user object from state.auth.user
 * @param {string|string[]} requiredPermission - A single permission string or an array of alternative permissions (OR logic)
 * @returns {boolean}
 */
export function hasPermission(user, requiredPermission) {
  if (!user) return false;

  const targetUser = user.user || user;

  // Owner or Admin role or wildcard permission has access to everything
  if (
    targetUser.isOwner ||
    targetUser.role === 'owner' ||
    targetUser.role === 'admin' ||
    (Array.isArray(targetUser.permissions) && targetUser.permissions.includes('*'))
  ) {
    return true;
  }

  const userPerms = Array.isArray(targetUser.permissions) ? targetUser.permissions : [];

  if (Array.isArray(requiredPermission)) {
    return requiredPermission.some((p) => userPerms.includes(p) || userPerms.includes('*'));
  }

  return userPerms.includes(requiredPermission) || userPerms.includes('*');
}

/**
 * Checks whether a user has administrative or workspace owner privileges.
 * @param {object} user - The logged in user object from state.auth.user
 * @param {object} [activeApp] - The active application workspace (optional)
 * @returns {boolean}
 */
export function isAdminOrOwner(user, activeApp = null) {
  if (!user) return false;

  const targetUser = user.user || user;

  // Direct owner/admin flags on user object
  if (
    Boolean(targetUser.isOwner) ||
    targetUser.role === 'owner' ||
    targetUser.role === 'admin' ||
    targetUser.role === 'superadmin' ||
    (Array.isArray(targetUser.permissions) && targetUser.permissions.includes('*'))
  ) {
    return true;
  }

  // Check active application owner
  if (activeApp && activeApp.owner) {
    const ownerId =
      typeof activeApp.owner === 'object'
        ? activeApp.owner._id || activeApp.owner.id
        : activeApp.owner;
    const userId = targetUser._id || targetUser.id;
    if (ownerId && userId && String(ownerId) === String(userId)) {
      return true;
    }
  }

  return false;
}

export default hasPermission;
